import React, { useState, useCallback, useEffect } from 'react';
import {
    Box, Typography, Button, Paper, TextField, Grid,
    CircularProgress, Alert, ToggleButton, ToggleButtonGroup,
    MenuItem, Select, FormControl, InputLabel
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';

// ──────────────────────────────────────────────────────────────────────
// Intuit Payments API tokenization endpoint
// Card and bank account data go directly from browser to Intuit
// ──────────────────────────────────────────────────────────────────────

const INTUIT_TOKEN_ENDPOINTS = {
    sandbox: "https://sandbox.api.intuit.com/quickbooks/v4/payments/tokens",
    production: "https://api.intuit.com/quickbooks/v4/payments/tokens",
};

const INTUIT_ENVIRONMENT = process.env.REACT_APP_INTUIT_ENVIRONMENT || "sandbox";
const INTUIT_TOKEN_URL = INTUIT_TOKEN_ENDPOINTS[INTUIT_ENVIRONMENT];
const RECAPTCHA_V3_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "YOUR_SITE_KEY_HERE";

// ──────────────────────────────────────────────────────────────────────
// Credit Card Form
// ──────────────────────────────────────────────────────────────────────

function CreditCardForm({ cardData, updateCard, updateAddress, isProcessing }) {
    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <TextField
                    label="Cardholder Name"
                    value={cardData.name}
                    onChange={(e) => updateCard('name', e.target.value)}
                    fullWidth
                    required
                    disabled={isProcessing}
                    autoComplete="cc-name"
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                    label="Card Number"
                    value={cardData.number}
                    onChange={(e) => {
                        const val = e.target.value.replace(/[^\d\s]/g, '');
                        updateCard('number', val);
                    }}
                    fullWidth
                    required
                    disabled={isProcessing}
                    inputProps={{ maxLength: 19 }}
                    autoComplete="cc-number"
                    placeholder="4111 1111 1111 1111"
                />
            </Grid>
            <Grid item xs={4}>
                <TextField
                    label="Exp Month"
                    value={cardData.expMonth}
                    onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 2);
                        updateCard('expMonth', val);
                    }}
                    fullWidth
                    required
                    disabled={isProcessing}
                    placeholder="MM"
                    autoComplete="cc-exp-month"
                />
            </Grid>
            <Grid item xs={4}>
                <TextField
                    label="Exp Year"
                    value={cardData.expYear}
                    onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                        updateCard('expYear', val);
                    }}
                    fullWidth
                    required
                    disabled={isProcessing}
                    placeholder="YYYY"
                    autoComplete="cc-exp-year"
                />
            </Grid>
            <Grid item xs={4}>
                <TextField
                    label="CVC"
                    value={cardData.cvc}
                    onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                        updateCard('cvc', val);
                    }}
                    fullWidth
                    required
                    disabled={isProcessing}
                    inputProps={{ maxLength: 4 }}
                    autoComplete="cc-csc"
                    type="password"
                />
            </Grid>

            <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                    Billing Address
                </Typography>
            </Grid>
            <Grid item xs={12}>
                <TextField
                    label="Street Address"
                    value={cardData.address.streetAddress}
                    onChange={(e) => updateAddress('streetAddress', e.target.value)}
                    fullWidth
                    required
                    disabled={isProcessing}
                    autoComplete="street-address"
                />
            </Grid>
            <Grid item xs={6}>
                <TextField
                    label="City"
                    value={cardData.address.city}
                    onChange={(e) => updateAddress('city', e.target.value)}
                    fullWidth
                    required
                    disabled={isProcessing}
                    autoComplete="address-level2"
                />
            </Grid>
            <Grid item xs={3}>
                <TextField
                    label="State"
                    value={cardData.address.region}
                    onChange={(e) => updateAddress('region', e.target.value.toUpperCase().slice(0, 2))}
                    fullWidth
                    required
                    disabled={isProcessing}
                    placeholder="GA"
                    autoComplete="address-level1"
                />
            </Grid>
            <Grid item xs={3}>
                <TextField
                    label="Postal Code"
                    value={cardData.address.postalCode}
                    onChange={(e) => updateAddress('postalCode', e.target.value)}
                    fullWidth
                    required
                    disabled={isProcessing}
                    autoComplete="postal-code"
                />
            </Grid>
        </Grid>
    );
}

// ──────────────────────────────────────────────────────────────────────
// Bank Account Form
// ──────────────────────────────────────────────────────────────────────

function BankAccountForm({ bankData, updateBank, isProcessing }) {
    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <TextField
                    label="Account Holder Name"
                    value={bankData.name}
                    onChange={(e) => updateBank('name', e.target.value)}
                    fullWidth
                    required
                    disabled={isProcessing}
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                    label="Routing Number"
                    value={bankData.routingNumber}
                    onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 9);
                        updateBank('routingNumber', val);
                    }}
                    fullWidth
                    required
                    disabled={isProcessing}
                    inputProps={{ maxLength: 9 }}
                    placeholder="9 digits"
                    helperText="Your bank's 9-digit routing number"
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                    label="Account Number"
                    value={bankData.accountNumber}
                    onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 17);
                        updateBank('accountNumber', val);
                    }}
                    fullWidth
                    required
                    disabled={isProcessing}
                    inputProps={{ maxLength: 17 }}
                    placeholder="4-17 digits"
                />
            </Grid>
            <Grid item xs={12}>
                <FormControl fullWidth required disabled={isProcessing}>
                    <InputLabel>Account Type</InputLabel>
                    <Select
                        value={bankData.accountType}
                        label="Account Type"
                        onChange={(e) => updateBank('accountType', e.target.value)}
                    >
                        <MenuItem value="PERSONAL_CHECKING">Personal Checking</MenuItem>
                        <MenuItem value="PERSONAL_SAVINGS">Personal Savings</MenuItem>
                        <MenuItem value="BUSINESS_CHECKING">Business Checking</MenuItem>
                        <MenuItem value="BUSINESS_SAVINGS">Business Savings</MenuItem>
                    </Select>
                </FormControl>
            </Grid>
            <Grid item xs={12}>
                <TextField
                    label="Phone Number"
                    value={bankData.phone}
                    onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                        updateBank('phone', val);
                    }}
                    fullWidth
                    required
                    disabled={isProcessing}
                    inputProps={{ maxLength: 10 }}
                    placeholder="10 digits, no dashes"
                    helperText="Phone number associated with the account"
                />
            </Grid>
        </Grid>
    );
}

/**
 * Execute reCAPTCHA v3 and return the token.
 * Call this immediately before tokenization — tokens expire after 2 min.
 *
 * @param {string} action - The action name for analytics (e.g., "payment_capture")
 * @returns {Promise<string>} The reCAPTCHA token
 */
async function executeRecaptcha(action = "payment_capture") {
    return new Promise((resolve, reject) => {
        if (!window.grecaptcha) {
            reject(new Error("reCAPTCHA not loaded"));
            return;
        }

        window.grecaptcha.ready(() => {
            window.grecaptcha
                .execute(RECAPTCHA_V3_SITE_KEY, { action })
                .then(resolve)
                .catch(reject);
        });
    });
}

// ──────────────────────────────────────────────────────────────────────
// Main Payment Capture Component
// ──────────────────────────────────────────────────────────────────────

/**
 * PCI-compliant payment capture form supporting credit card and bank account.
 *
 * All sensitive data (card numbers, account numbers) is sent DIRECTLY to
 * Intuit's tokenization endpoint from the browser. Our servers never see
 * the raw payment data — only the resulting one-time token.
 *
 * Props:
 *   onTokenReceived(token, tokenType, recaptchaToken) - Called with tokens after tokenization
 *   isSubmitting                                      - Whether the parent is processing (disables form)
 *   error                                             - External error message to display
 */
export default function IntuitPaymentCapture({ onTokenReceived, isSubmitting = false, error: externalError }) {
    const [paymentMethod, setPaymentMethod] = useState('card');
    const [tokenizing, setTokenizing] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        // Skip if already loaded
        if (window.grecaptcha) return;

        const script = document.createElement('script');
        script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_V3_SITE_KEY}`;
        script.async = true;
        document.head.appendChild(script);

        return () => {
            // Optional cleanup — reCAPTCHA v3 badge stays in DOM by design
        };
    }, []);

    // Card state
    const [cardData, setCardData] = useState({
        name: '', number: '', expMonth: '', expYear: '', cvc: '',
        address: { streetAddress: '', city: '', region: '', postalCode: '', country: 'US' },
    });

    // Bank account state
    const [bankData, setBankData] = useState({
        name: '', routingNumber: '', accountNumber: '', accountType: 'BUSINESS_CHECKING', phone: '',
    });

    const updateCard = (field, value) => setCardData(prev => ({ ...prev, [field]: value }));
    const updateAddress = (field, value) => setCardData(prev => ({
        ...prev, address: { ...prev.address, [field]: value },
    }));
    const updateBank = (field, value) => setBankData(prev => ({ ...prev, [field]: value }));

    // ── Validation ──

    const validateCard = () => {
        if (!cardData.name.trim()) return "Cardholder name is required";
        if (!cardData.number.trim() || cardData.number.replace(/\s/g, '').length < 13) return "Valid card number is required";
        if (!cardData.expMonth || cardData.expMonth < 1 || cardData.expMonth > 12) return "Valid expiration month is required";
        if (!cardData.expYear || cardData.expYear.length !== 4) return "Valid 4-digit expiration year is required";
        if (!cardData.cvc.trim() || cardData.cvc.length < 3) return "Valid CVC is required";
        if (!cardData.address.postalCode.trim()) return "Postal code is required";
        return null;
    };

    const validateBank = () => {
        if (!bankData.name.trim()) return "Account holder name is required";
        if (!bankData.routingNumber || bankData.routingNumber.length !== 9) return "Valid 9-digit routing number is required";
        if (!bankData.accountNumber || bankData.accountNumber.length < 4) return "Valid account number is required (minimum 4 digits)";
        if (!bankData.accountType) return "Account type is required";
        if (!bankData.phone || bankData.phone.length !== 10) return "Valid 10-digit phone number is required";
        return null;
    };

    // ── Tokenization ──

    const handleTokenize = useCallback(async () => {
        setError(null);

        const validationError = paymentMethod === 'card' ? validateCard() : validateBank();
        if (validationError) {
            setError(validationError);
            return;
        }

        setTokenizing(true);

        try {
            // ──────────────────────────────────────────────────────
            // Step 1: Execute reCAPTCHA v3 (before Intuit tokenization)
            // ──────────────────────────────────────────────────────

            let recaptchaToken;
            try {
                recaptchaToken = await executeRecaptcha("payment_capture");
            } catch (err) {
                console.error("reCAPTCHA execution failed:", err);
                throw new Error("Security verification failed. Please refresh the page and try again.");
            }

            // ──────────────────────────────────────────────────────
            // Step 2: PCI-CRITICAL PATH
            // Payment data goes directly from browser to Intuit.
            // Our server never sees raw card/account numbers.
            // ──────────────────────────────────────────────────────

            let tokenRequest;
            if (paymentMethod === 'card') {
                tokenRequest = {
                    card: {
                        name: cardData.name.trim(),
                        number: cardData.number.replace(/\s/g, ''),
                        expMonth: String(cardData.expMonth).padStart(2, '0'),
                        expYear: String(cardData.expYear),
                        cvc: cardData.cvc.trim(),
                        address: {
                            streetAddress: cardData.address.streetAddress.trim(),
                            city: cardData.address.city.trim(),
                            region: cardData.address.region.trim(),
                            postalCode: cardData.address.postalCode.trim(),
                            country: cardData.address.country,
                        },
                    },
                };
            } else {
                tokenRequest = {
                    bankAccount: {
                        name: bankData.name.trim(),
                        routingNumber: bankData.routingNumber,
                        accountNumber: bankData.accountNumber,
                        accountType: bankData.accountType,
                        phone: bankData.phone,
                    },
                };
            }

            const response = await fetch(INTUIT_TOKEN_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(tokenRequest),
            });

            if (!response.ok) {
                const errorBody = await response.text();
                console.error('Intuit tokenization failed:', response.status, errorBody);
                throw new Error('Payment tokenization failed. Please check your details and try again.');
            }

            const tokenResponse = await response.json();
            const token = tokenResponse.value;

            if (!token) {
                throw new Error('No token received from payment processor.');
            }

            // ──────────────────────────────────────────────────────
            // Step 3: Pass both tokens to parent
            // ──────────────────────────────────────────────────────

            console.log('Payment tokenized successfully');

            const tokenType = paymentMethod === 'card' ? 'CreditCard' : 'BankAccount';
            onTokenReceived(token, tokenType, recaptchaToken);

        } catch (e) {
            console.error('Tokenization error:', e);
            setError(e.message || 'Failed to process payment method. Please try again.');
        } finally {
            setTokenizing(false);
        }
    }, [cardData, bankData, paymentMethod, onTokenReceived]);

    const isProcessing = tokenizing || isSubmitting;
    const displayError = error || externalError;

    return (
        <Paper sx={{ p: 3, mt: 2 }} elevation={1}>
            {/* Payment method toggle */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <ToggleButtonGroup
                    value={paymentMethod}
                    exclusive
                    onChange={(e, val) => { if (val) { setPaymentMethod(val); setError(null); } }}
                    disabled={isProcessing}
                >
                    <ToggleButton value="card" sx={{ px: 3 }}>
                        <CreditCardIcon sx={{ mr: 1 }} />
                        Credit Card
                    </ToggleButton>
                    <ToggleButton value="bank" sx={{ px: 3 }}>
                        <AccountBalanceIcon sx={{ mr: 1 }} />
                        Bank Account
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Your {paymentMethod === 'card' ? 'card' : 'bank account'} information is sent
                directly to our payment processor (Intuit) and never stored on our servers.
            </Typography>

            {/* Form */}
            {paymentMethod === 'card' ? (
                <CreditCardForm
                    cardData={cardData}
                    updateCard={updateCard}
                    updateAddress={updateAddress}
                    isProcessing={isProcessing}
                />
            ) : (
                <BankAccountForm
                    bankData={bankData}
                    updateBank={updateBank}
                    isProcessing={isProcessing}
                />
            )}

            {displayError && (
                <Alert severity="error" sx={{ mt: 2 }}>{displayError}</Alert>
            )}

            <Box sx={{ mt: 3 }}>
                <Button
                    variant="contained"
                    onClick={handleTokenize}
                    disabled={isProcessing}
                    fullWidth
                    size="large"
                >
                    {isProcessing ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CircularProgress size={20} color="inherit" />
                            Processing...
                        </Box>
                    ) : (
                        'Save Payment Method'
                    )}
                </Button>
            </Box>

            {/* reCAPTCHA v3 attribution (required when badge is hidden via CSS) */}
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
                This site is protected by reCAPTCHA and the Google{' '}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> and{' '}
                <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a> apply.
            </Typography>
        </Paper>
    );
}